<template>
  <div class="h-full bg-black text-white flex flex-col">
    <div class="flex items-center justify-between px-4 py-3">
      <button
        type="button"
        class="text-base font-medium cursor-pointer"
        @click="$emit('close')"
      >
        {{ $t('label.cancel') }}
      </button>
      <span class="text-sm text-zinc-300">{{ $t('title.qr_scan') }}</span>
    </div>

    <div class="relative flex-1 min-h-0">
      <QrcodeStream
        class="h-full w-full"
        :paused="scanPaused || resolvingScan"
        @camera-on="onCameraOn"
        @detect="onDetect"
        @error="onScannerError"
      />

      <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div class="h-56 w-56 max-w-[70vw] max-h-[70vw] rounded-2xl border-2 border-white/70" />
      </div>
    </div>

    <div v-if="scanError" class="px-4 py-3 text-sm text-red-300">
      {{ scanError }}
    </div>
  </div>
</template>

<script>
import api from '@/utils/api.js';
import { QrcodeStream } from 'vue-qrcode-reader';

export default {
  name: 'MobileQrScanner',
  components: {
    QrcodeStream,
  },
  props: {
    employeeMode: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close'],

  data() {
    return {
      scanPaused: false,
      resolvingScan: false,
      scanError: null,
    };
  },

  methods: {
    onCameraOn() {
      this.scanError = null;
    },

    onScannerError() {
      this.scanError = this.$t('message.qr_scan_camera_error');
    },

    async onDetect(detectedCodes) {
      const scannedValue = detectedCodes?.[0]?.rawValue?.trim();
      if (!scannedValue || this.resolvingScan) {
        return;
      }

      this.scanPaused = true;
      await this.resolveAndNavigate(scannedValue);
    },

    parseProjectRoute(value) {
      let normalizedPath = value.trim();

      if (/^https?:\/\//i.test(value)) {
        const url = new URL(value);
        normalizedPath = url.pathname;
      }

      const parts = normalizedPath.split('/').filter(Boolean);

      if (parts[0] !== 'projects' || !parts[1]) {
        return null;
      }

      const reference = decodeURIComponent(parts[1]);
      if (parts[2] === 'matrix') {
        return { type: 'matrix', reference };
      }
      if (parts[2] === 'tasks' && parts[3]) {
        return { type: 'task', reference, taskReference: decodeURIComponent(parts[3]) };
      }

      return { type: 'project', reference };
    },

    async resolveTaskReference(taskReference) {
      const { data } = await api.get('/project-tasks/resolve', {
        params: { reference: taskReference },
      });

      return data?.match || null;
    },

    taskRouteLocation(projectReference, taskReference) {
      if (this.employeeMode) {
        return {
          name: 'EmployeeProjectTaskDetails',
          params: {
            reference: projectReference,
            taskReference,
          },
          query: { from: 'scan' },
        };
      }

      return {
        name: 'ProjectTaskDetails',
        params: {
          reference: projectReference,
          taskReference,
        },
        query: { from: 'scan' },
      };
    },

    async navigateAndClose(location) {
      await this.$router.push(location);
      this.$emit('close');
    },

    assertProjectAccess(message) {
      if (this.employeeMode) {
        throw new Error(message);
      }
    },

    async navigateProjectRoute(projectRoute) {
      if (projectRoute.type === 'matrix') {
        this.assertProjectAccess('Project matrix is not available for employees');
        await this.navigateAndClose({
          name: 'ProjectProgressMatrix',
          params: { reference: projectRoute.reference },
        });
        return;
      }

      if (projectRoute.type === 'project') {
        this.assertProjectAccess('Project pages are not available for employees');
        await this.navigateAndClose({
          name: 'ProjectDetails',
          params: { reference: projectRoute.reference },
        });
        return;
      }

      await this.navigateAndClose(
        this.taskRouteLocation(projectRoute.reference, projectRoute.taskReference),
      );
    },

    async navigateTaskReference(taskReference) {
      const match = await this.resolveTaskReference(taskReference);
      if (!match?.projectReference) {
        throw new Error('Task not found');
      }

      await this.navigateAndClose(this.taskRouteLocation(
        match.projectReference,
        match.taskReference || taskReference,
      ));
    },

    async navigateUnqualifiedReference(candidateReference) {
      if (this.employeeMode) {
        await this.navigateTaskReference(candidateReference);
        return;
      }

      try {
        await api.get(`/projects/${encodeURIComponent(candidateReference)}`);
        await this.navigateAndClose({
          name: 'ProjectDetails',
          params: { reference: candidateReference },
        });
      } catch {
        await this.navigateTaskReference(candidateReference);
      }
    },

    async navigateScannedValue(rawValue) {
      const projectRoute = this.parseProjectRoute(rawValue);
      if (projectRoute) {
        await this.navigateProjectRoute(projectRoute);
        return;
      }

      const normalizedValue = rawValue.trim();
      const lowerCaseValue = normalizedValue.toLowerCase();
      if (lowerCaseValue.startsWith('project:')) {
        this.assertProjectAccess('Project QR is not available for employees');
        const reference = normalizedValue.slice('project:'.length).trim();
        if (!reference) {
          throw new Error('Invalid project qr');
        }
        await this.navigateAndClose({ name: 'ProjectDetails', params: { reference } });
        return;
      }

      if (lowerCaseValue.startsWith('task:')) {
        await this.navigateTaskReference(normalizedValue.slice('task:'.length).trim());
        return;
      }

      await this.navigateUnqualifiedReference(normalizedValue);
    },

    async resolveAndNavigate(rawValue) {
      if (this.resolvingScan) {
        return;
      }

      this.resolvingScan = true;
      this.scanError = null;

      try {
        await this.navigateScannedValue(rawValue);
      } catch {
        this.scanError = this.$t('message.qr_scan_unknown');
      } finally {
        this.scanPaused = false;
        this.resolvingScan = false;
      }
    },
  },
};
</script>
